/**
 * Πλακίδια μετάπτωσης ανά κατάστημα (§13.19.Ι).
 *
 * ΓΙΑΤΙ ΞΕΧΩΡΙΣΤΗ ΓΡΑΜΜΗ ΚΑΙ ΟΧΙ ΚΟΥΜΠΙ ΜΕΣΑ ΣΤΟ ΠΛΕΓΜΑ: η μετάπτωση είναι
 * ενέργεια ΚΑΤΑΣΤΗΜΑΤΟΣ, γίνεται ΜΙΑ ΦΟΡΑ και ΔΕΝ αναιρείται. Το κουμπί δεν
 * επιτρέπεται να κάθεται δίπλα σε καθημερινές ενέργειες τιμής/διαθεσιμότητας.
 *
 * Τα τρία χρώματα λένε τρία διαφορετικά πράγματα και κανένα δεν είναι «εντάξει»:
 *  • πράσινο  = έγινε μετάπτωση (από εδώ και πέρα ΜΟΝΟ αλλαγές, όχι ingest),
 *  • γκρι     = δεν έχει μεταπτωθεί (μόνο ingest μπορεί να σταλεί),
 *  • άσπρο/—  = ο server δεν στέλνει ακόμη `migrated_at`· ΔΕΝ το λέμε «όχι».
 */

import React from 'react';
import { Rocket, ShieldAlert } from 'lucide-react';
import { StoreSummaryAny } from '../../services/platformCatalogApi';
import { fmtDateTime, storeShort } from './catalogUi';

interface Props {
  stores: StoreSummaryAny[];
  /** org_owner: μόνο αυτός τρέχει μετάπτωση (§13.19.Θ). */
  canMigrate: boolean;
  /** Ο server έχει άδεια εξερχόμενης εγγραφής (πύλες 1-3). */
  serverCanPush: boolean;
  busyStoreId: number | null;
  onMigrate: (storeId: number) => void;
}

const MigrationTiles: React.FC<Props> = ({ stores, canMigrate, serverCanPush, busyStoreId, onMigrate }) => {
  const withCreds = stores.filter(s => s.has_credentials);
  if (withCreds.length === 0) return null;

  const migrated = withCreds.filter(s => !!s.migrated_at);
  const unknown = withCreds.filter(s => s.migrated_at === undefined);

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-t3">
        <Rocket className="w-3.5 h-3.5" />
        <span>
          Μετάπτωση σε δικούς μας κωδικούς:{' '}
          <strong className="text-t2">{migrated.length}</strong>/{withCreds.length} καταστήματα
        </span>
        {unknown.length === withCreds.length && (
          <span className="text-t4">— ο server δεν στέλνει ακόμη την κατάσταση μετάπτωσης</span>
        )}
        {!serverCanPush && (
          <span className="flex items-center gap-1 text-t4">
            <ShieldAlert className="w-3.5 h-3.5" /> αυτός ο server δεν στέλνει προς τα έξω
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {withCreds.map(s => {
          const isMigrated = !!s.migrated_at;
          const isUnknown = s.migrated_at === undefined;
          const neverImported = s.last_import_at === null;
          const busy = busyStoreId === s.store_id;
          const disabled = !canMigrate || isMigrated || neverImported || busy;
          const title = [
            s.store_name,
            isMigrated
              ? `Μετάπτωση: ${fmtDateTime(s.migrated_at)} · ${s.items} είδη στο efood`
              : (isUnknown ? 'Άγνωστη κατάσταση μετάπτωσης (παλιότερος server)' : 'Δεν έχει μεταπτωθεί'),
            isMigrated
              ? 'Από εδώ και πέρα στέλνονται ΜΟΝΟ αλλαγές (deltas) — ποτέ δεύτερο ingest.'
              : 'Πριν τη μετάπτωση, το κατάστημα μπορεί να σταλεί ΜΟΝΟ με ολική αντικατάσταση (ingest).',
            neverImported ? 'Χρειάζεται πρώτα εισαγωγή από το efood.' : '',
            s.last_price_sync_at ? `Τελευταίος συγχρονισμός τιμών: ${fmtDateTime(s.last_price_sync_at)}` : '',
            s.price_mismatches !== undefined && s.price_mismatches !== null
              ? `Διαφορές τιμής με τον κατάλογο: ${s.price_mismatches}` : '',
            !canMigrate ? 'Η μετάπτωση γίνεται μόνο από τον ιδιοκτήτη του οργανισμού.' : '',
          ].filter(Boolean).join('\n');

          return (
            <button
              key={s.store_id}
              type="button"
              disabled={disabled}
              onClick={() => onMigrate(s.store_id)}
              title={title}
              className={`px-2 py-1 rounded-lg text-[11px] border flex items-center gap-1 disabled:cursor-default ${
                isMigrated
                  ? 'bg-emerald-500/10 border-emerald-300 dark:border-emerald-700/50 text-emerald-700 dark:text-emerald-300'
                  : isUnknown
                    ? 'bg-surface-2 border-line text-t4'
                    : 'bg-surface-2 border-line-strong text-t2'}`}
            >
              <span className="font-medium">{storeShort(s.store_name)}</span>
              <span className="opacity-80">
                {isMigrated
                  ? `μετάπτωση ${fmtDateTime(s.migrated_at).split(',')[0]}`
                  : (isUnknown ? '—' : 'όχι ακόμη')}
              </span>
              {s.price_mismatches !== undefined && s.price_mismatches !== null && s.price_mismatches > 0 && (
                <span
                  className="px-1 rounded bg-amber-500/25 text-amber-800 dark:text-amber-200"
                  title="Διαφορές τιμής με τον κατάλογο"
                >
                  ≠{s.price_mismatches}
                </span>
              )}
              {!disabled && <Rocket className="w-3 h-3" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MigrationTiles;
